import { Calculator, Users, BookOpen, Scale } from "lucide-react";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AnimatedHeadline from "@/components/AnimatedHeadline";

const services = [
  {
    num: "01",
    icon: Calculator,
    title: "Asesoría Fiscal",
    description:
      "Planificación y optimización fiscal para maximizar tus beneficios. Declaraciones trimestrales y anuales, IVA, IRPF, Impuesto de Sociedades y representación ante la Agencia Tributaria.",
  },
  {
    num: "02",
    icon: Users,
    title: "Asesoría Laboral",
    description:
      "Gestión integral de nóminas, contratos, altas y bajas en Seguridad Social, y asesoramiento en relaciones laborales y recursos humanos.",
  },
  {
    num: "03",
    icon: BookOpen,
    title: "Asesoría Contable",
    description:
      "Llevanza de contabilidad, elaboración de cuentas anuales, informes financieros y control de gestión adaptado a tu actividad.",
  },
  {
    num: "04",
    icon: Scale,
    title: "Asesoría Jurídica",
    description:
      "Asesoramiento legal en constitución de sociedades, contratos mercantiles, reclamaciones y consultas jurídicas generales.",
  },
];

export default function Services() {
  const sectionRef  = useRef(null);
  const labelRef    = useRef(null);
  const goldLineRef = useRef(null);
  const cardsRef    = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Label fade
      gsap.fromTo(labelRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
          scrollTrigger: { trigger: labelRef.current, start: "top 87%" } });

      // Gold line draw
      gsap.fromTo(goldLineRef.current,
        { scaleX: 0, transformOrigin: "left center" },
        { scaleX: 1, duration: 0.9, ease: "power2.out",
          scrollTrigger: { trigger: goldLineRef.current, start: "top 85%" } });

      // Cards — alternating left / right
      if (cardsRef.current) {
        Array.from(cardsRef.current.children).forEach((card, idx) => {
          const fromX = idx % 2 === 0 ? -80 : 80;
          gsap.fromTo(card,
            { opacity: 0, x: fromX, rotationY: idx % 2 === 0 ? -5 : 5 },
            { opacity: 1, x: 0, rotationY: 0, duration: 0.9, ease: "power3.out",
              scrollTrigger: { trigger: card, start: "top 88%" } });
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="servicios"
      ref={sectionRef}
      data-testid="services-section"
      className="py-28 md:py-36 bg-white">

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <span ref={labelRef} className="text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ fontFamily: "'Manrope', sans-serif", opacity: 0 }}>
              Nuestros servicios
            </span>
            <AnimatedHeadline
              text="Soluciones completas para tu negocio"
              data-testid="services-title"
              className="text-3xl sm:text-4xl md:text-[3.25rem] font-light tracking-tight mt-5 text-[#1a1a1a] leading-[1.15] max-w-lg"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            />
          </div>
          <div className="hidden lg:block">
            <div ref={goldLineRef} className="w-20 h-[2px] bg-[#c9a96e]" style={{ transformOrigin: "left center" }} />
          </div>
        </div>

        {/* Cards grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((service, idx) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                data-testid={`service-card-${idx}`}
                className="group relative rounded-sm border service-card overflow-hidden bg-[#1a1a1a] border-[#1a1a1a]"
                style={{ opacity: 0 }}>
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#c9a96e] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                <div className="p-8 md:p-10">
                  <div className="flex items-start justify-between mb-8">
                    <span className="text-[10px] font-mono tracking-[0.2em] text-[#c9a96e]/50">{service.num}</span>
                    <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-[#c9a96e]/10 border border-[#c9a96e]/20 transition-all duration-300">
                      <Icon className="w-5 h-5 stroke-[1.5] text-[#c9a96e]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-normal mb-4 text-[#f8f6f3]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {service.title}
                  </h3>
                  <p className="text-[13px] text-[#f8f6f3]/50 font-light leading-[1.8]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
